// frontend/src/features/campaign/components/create/CampaignTradingChart.js
import React, { useMemo } from 'react';

export default function CampaignTradingChart({
  formData,
  calculatedKPIs,
  activeScenario = 'baseline',
}) {
  // Extract raw numerical values from formatted KPIs
  const basePipelineNumeric = useMemo(() => {
    if (
      calculatedKPIs?.estimatedPipelineRaw !== undefined &&
      calculatedKPIs?.estimatedPipelineRaw !== null
    ) {
      return Math.max(0, Number(calculatedKPIs.estimatedPipelineRaw) || 0);
    }
    if (!calculatedKPIs?.pipelineValue) return 0;
    const str = String(calculatedKPIs.pipelineValue).replace(/[^0-9.]/g, '');
    const num = parseFloat(str);
    return isNaN(num) || num < 0 ? 0 : num;
  }, [calculatedKPIs?.estimatedPipelineRaw, calculatedKPIs?.pipelineValue]);

  const reachableNumeric = useMemo(() => {
    if (calculatedKPIs?.counts?.reachable !== undefined) {
      return Math.max(0, Number(calculatedKPIs.counts.reachable) || 0);
    }
    const str = String(calculatedKPIs?.reachable || '0').replace(/[^0-9]/g, '');
    const num = parseInt(str, 10);
    return isNaN(num) || num < 0 ? 0 : num;
  }, [calculatedKPIs?.counts?.reachable, calculatedKPIs?.reachable]);

  const audienceNumeric = useMemo(() => {
    if (calculatedKPIs?.counts?.audience !== undefined) {
      return Math.max(0, Number(calculatedKPIs.counts.audience) || 0);
    }
    const str = String(formData?.targetAudience || '0').replace(/[^0-9]/g, '');
    const num = parseInt(str, 10);
    return isNaN(num) || num < 0 ? 0 : num;
  }, [calculatedKPIs?.counts?.audience, formData?.targetAudience]);

  const meetingsNumeric = useMemo(() => {
    if (calculatedKPIs?.counts?.meetings !== undefined) {
      return Math.max(0, Number(calculatedKPIs.counts.meetings) || 0);
    }
    const str = String(calculatedKPIs?.meetings || '0').replace(/[^0-9]/g, '');
    const num = parseInt(str, 10);
    return isNaN(num) || num < 0 ? 0 : num;
  }, [calculatedKPIs?.counts?.meetings, calculatedKPIs?.meetings]);

  const opportunitiesNumeric = useMemo(() => {
    if (calculatedKPIs?.counts?.opportunities !== undefined) {
      return Math.max(0, Number(calculatedKPIs.counts.opportunities) || 0);
    }
    const str = String(calculatedKPIs?.opportunities || '0').replace(/[^0-9]/g, '');
    const num = parseInt(str, 10);
    return isNaN(num) || num < 0 ? 0 : num;
  }, [calculatedKPIs?.counts?.opportunities, calculatedKPIs?.opportunities]);

  const budgetNumeric = useMemo(() => {
    if (calculatedKPIs?.financials?.allocatedBudgetRaw !== undefined) {
      return Math.max(0, Number(calculatedKPIs.financials.allocatedBudgetRaw) || 0);
    }
    const val = formData?.allocatedBudget || '0';
    const str = String(val).replace(/[^0-9.]/g, '');
    const num = parseFloat(str);
    return isNaN(num) || num < 0 ? 0 : num;
  }, [calculatedKPIs?.financials?.allocatedBudgetRaw, formData?.allocatedBudget]);

  const roiNumeric = useMemo(() => {
    if (!calculatedKPIs?.financials?.roiMultiple) return 0;
    const str = String(calculatedKPIs.financials.roiMultiple).replace(/[^0-9.]/g, '');
    const num = parseFloat(str);
    return isNaN(num) || num < 0 ? 0 : num;
  }, [calculatedKPIs?.financials?.roiMultiple]);

  const cpmNumeric = useMemo(() => {
    if (!calculatedKPIs?.financials?.formattedCostPerMeeting) {
      return meetingsNumeric > 0 ? Math.round(budgetNumeric / meetingsNumeric) : 0;
    }
    const str = String(calculatedKPIs.financials.formattedCostPerMeeting).replace(/[^0-9.]/g, '');
    const num = parseFloat(str);
    return isNaN(num) || num < 0 ? 0 : num;
  }, [calculatedKPIs?.financials?.formattedCostPerMeeting, budgetNumeric, meetingsNumeric]);

  const reachabilityRate = useMemo(() => {
    if (audienceNumeric <= 0) return reachableNumeric > 0 ? 80 : 0;
    return Math.min(100, Math.round((reachableNumeric / audienceNumeric) * 1000) / 10);
  }, [reachableNumeric, audienceNumeric]);

  const meetingRate = useMemo(() => {
    if (reachableNumeric <= 0) return 0;
    return Math.min(100, Math.round((meetingsNumeric / reachableNumeric) * 1000) / 10);
  }, [meetingsNumeric, reachableNumeric]);

  const formatCurrency = (val) => {
    if (val >= 1000000) return 'RM ' + (val / 1000000).toFixed(1) + 'M';
    if (val >= 1000) return 'RM ' + (val / 1000).toFixed(0) + 'k';
    return 'RM ' + val;
  };

  // Speedometer 1 Math: Pipeline Realization
  const pipelineTarget = basePipelineNumeric > 0 ? Math.round(basePipelineNumeric * 0.85) : 80000;
  const pipelineAttainmentRatio = pipelineTarget > 0 ? basePipelineNumeric / pipelineTarget : 1;
  const gauge1Ratio = Math.max(0, Math.min(1, pipelineAttainmentRatio / 1.5));
  // Needle points UP at rest and rotates from -90deg (0%) to +90deg (150% max)
  const needle1Angle = -90 + gauge1Ratio * 180;
  const speedometer1Circumference = Math.PI * 65; // ~204.2
  const speedometer1Dash = gauge1Ratio * speedometer1Circumference;

  // Speedometer 2 Math: ROI & Capital Efficiency (Scale: 0 to 10x ROI)
  const roiMax = 10;
  const gauge2Ratio = Math.max(0, Math.min(1, roiNumeric / roiMax));
  const needle2Angle = -90 + gauge2Ratio * 180;
  const speedometer2Circumference = Math.PI * 65;
  const speedometer2Dash = gauge2Ratio * speedometer2Circumference;

  // Dynamic ROI Badge based on actual performance
  const roiBadgeInfo = useMemo(() => {
    if (roiNumeric >= 3.0) {
      return { label: 'High Yield', className: 'high-yield', icon: 'fa-bolt' };
    }
    if (roiNumeric >= 1.5) {
      return { label: 'Moderate', className: 'moderate', icon: 'fa-chart-line' };
    }
    return { label: 'Ramp-Up', className: 'ramp-up', icon: 'fa-arrow-trend-up' };
  }, [roiNumeric]);

  // Donut 1 Math: Reachability Rate
  const donutCircumference = 2 * Math.PI * 44; // ~276.46
  const reachabilityDash = (reachabilityRate / 100) * donutCircumference;

  // Donut 2 Math: Meeting Yield
  const meetingYieldTarget = 100; // full 0-100%
  const meetingYieldRatio = Math.min(1, meetingRate / meetingYieldTarget);
  const meetingYieldDash = meetingYieldRatio * donutCircumference;

  return (
    <div className="cmp-kpi-forecast-graph-card">
      {/* Clean Top SaaS Header */}
      <div className="cmp-kpi-graph-top-row">
        <div className="cmp-kpi-graph-headline">
          <div className="cmp-kpi-headline-meta">
            <span className="cmp-kpi-tag-chip">FORECAST TRAJECTORY</span>
            <span className="cmp-kpi-scenario-chip capitalize">
              {activeScenario
                ? activeScenario.charAt(0).toUpperCase() + activeScenario.slice(1)
                : 'Baseline'}{' '}
              Model
            </span>
          </div>
          <div className="cmp-kpi-headline-val-row">
            <span className="cmp-kpi-headline-val">{calculatedKPIs?.pipelineValue || 'RM 0'}</span>
            {calculatedKPIs?.financials?.roiMultiple && (
              <span className="cmp-kpi-roi-pill">
                <i className="fas fa-arrow-trend-up" /> {calculatedKPIs.financials.roiMultiple} ROI
              </span>
            )}
          </div>
          <span className="cmp-kpi-headline-sub">
            Real-time pipeline metrics and conversion performance indicators based on verified
            reachability.
          </span>
        </div>
      </div>

      {/* EXECUTIVE DASHBOARD COCKPIT (Gauges, Donut Rings, Sparklines & Bullet Range Bars) */}
      <div className="cmp-exec-cockpit-view">
        {/* Top Row: 4 Executive Gauges & Radial Progress Meters */}
        <div className="cmp-exec-gauges-grid">
          {/* Speedometer 1: Pipeline Realization Meter */}
          <div className="cmp-exec-gauge-card">
            <div className="cmp-exec-gauge-header">
              <span className="cmp-exec-gauge-title">
                <i className="fas fa-chart-pie" /> Pipeline Goal
              </span>
              <span className="cmp-exec-status-badge optimal">
                <i className="fas fa-circle-check" /> {Math.round(pipelineAttainmentRatio * 100)}%
                Target
              </span>
            </div>
            <div className="cmp-exec-gauge-svg-wrap">
              <svg viewBox="0 0 200 115" className="cmp-exec-speedometer-svg">
                <defs>
                  <linearGradient id="gaugeGradient1" x1="0%" y1="0%" x2="100%" y2="0%">
                    <stop offset="0%" stopColor="#0284c7" />
                    <stop offset="50%" stopColor="#0ea5e9" />
                    <stop offset="100%" stopColor="#10b981" />
                  </linearGradient>
                </defs>
                {/* Outer Graduated Ticks */}
                {[0, 30, 60, 90, 120, 150, 180].map((deg, i) => {
                  const rad = ((deg - 180) * Math.PI) / 180;
                  const x1 = 100 + 74 * Math.cos(rad);
                  const y1 = 90 + 74 * Math.sin(rad);
                  const x2 = 100 + (i % 3 === 0 ? 82 : 78) * Math.cos(rad);
                  const y2 = 90 + (i % 3 === 0 ? 82 : 78) * Math.sin(rad);
                  return (
                    <line
                      key={i}
                      x1={x1}
                      y1={y1}
                      x2={x2}
                      y2={y2}
                      stroke="#cbd5e1"
                      strokeWidth={i % 3 === 0 ? '2' : '1'}
                    />
                  );
                })}
                {/* Background Arc */}
                <path
                  d="M 35 90 A 65 65 0 0 1 165 90"
                  fill="none"
                  stroke="#f1f5f9"
                  strokeWidth="14"
                  strokeLinecap="round"
                />
                {/* Colored Value Arc */}
                <path
                  d="M 35 90 A 65 65 0 0 1 165 90"
                  fill="none"
                  stroke="url(#gaugeGradient1)"
                  strokeWidth="14"
                  strokeLinecap="round"
                  strokeDasharray={`${speedometer1Dash} 300`}
                />
                {/* Needle Pivot (Drawn pointing straight UP, rotated by needle1Angle) */}
                <g
                  transform={`rotate(${needle1Angle}, 100, 90)`}
                  style={{ transition: 'transform 0.5s cubic-bezier(0.4, 0, 0.2, 1)' }}
                >
                  <line
                    x1="100"
                    y1="90"
                    x2="100"
                    y2="36"
                    stroke="#0f172a"
                    strokeWidth="3"
                    strokeLinecap="round"
                  />
                  <polygon points="100,32 96,40 104,40" fill="#0f172a" />
                </g>
                {/* Center Pivot Hub */}
                <circle cx="100" cy="90" r="7" fill="#0f172a" />
                <circle cx="100" cy="90" r="3" fill="#ffffff" />
              </svg>
            </div>
            <div className="cmp-exec-gauge-footer">
              <div className="cmp-exec-gauge-primary-val">
                {calculatedKPIs?.pipelineValue || 'RM 0'}
              </div>
              <div className="cmp-exec-gauge-subtext">Target: {formatCurrency(pipelineTarget)}</div>
            </div>
          </div>

          {/* Speedometer 2: ROI & Capital Efficiency */}
          <div className="cmp-exec-gauge-card">
            <div className="cmp-exec-gauge-header">
              <span className="cmp-exec-gauge-title">
                <i className="fas fa-coins" /> Capital ROI
              </span>
              <span className={`cmp-exec-status-badge ${roiBadgeInfo.className}`}>
                <i className={`fas ${roiBadgeInfo.icon}`} /> {roiBadgeInfo.label}
              </span>
            </div>
            <div className="cmp-exec-gauge-svg-wrap">
              <svg viewBox="0 0 200 115" className="cmp-exec-speedometer-svg">
                <defs>
                  <linearGradient id="gaugeGradient2" x1="0%" y1="0%" x2="100%" y2="0%">
                    <stop offset="0%" stopColor="#ef4444" />
                    <stop offset="35%" stopColor="#f59e0b" />
                    <stop offset="70%" stopColor="#10b981" />
                    <stop offset="100%" stopColor="#059669" />
                  </linearGradient>
                </defs>
                {/* Outer Graduated Ticks */}
                {[0, 36, 72, 108, 144, 180].map((deg, i) => {
                  const rad = ((deg - 180) * Math.PI) / 180;
                  const x1 = 100 + 74 * Math.cos(rad);
                  const y1 = 90 + 74 * Math.sin(rad);
                  const x2 = 100 + 82 * Math.cos(rad);
                  const y2 = 90 + 82 * Math.sin(rad);
                  return (
                    <line
                      key={i}
                      x1={x1}
                      y1={y1}
                      x2={x2}
                      y2={y2}
                      stroke="#cbd5e1"
                      strokeWidth="1.5"
                    />
                  );
                })}
                {/* Background Arc */}
                <path
                  d="M 35 90 A 65 65 0 0 1 165 90"
                  fill="none"
                  stroke="#f1f5f9"
                  strokeWidth="14"
                  strokeLinecap="round"
                />
                {/* Colored Arc */}
                <path
                  d="M 35 90 A 65 65 0 0 1 165 90"
                  fill="none"
                  stroke="url(#gaugeGradient2)"
                  strokeWidth="14"
                  strokeLinecap="round"
                  strokeDasharray={`${speedometer2Dash} 300`}
                />
                {/* Needle Pivot (Drawn pointing straight UP, rotated by needle2Angle) */}
                <g
                  transform={`rotate(${needle2Angle}, 100, 90)`}
                  style={{ transition: 'transform 0.5s cubic-bezier(0.4, 0, 0.2, 1)' }}
                >
                  <line
                    x1="100"
                    y1="90"
                    x2="100"
                    y2="36"
                    stroke="#0f172a"
                    strokeWidth="3"
                    strokeLinecap="round"
                  />
                  <polygon points="100,32 96,40 104,40" fill="#0f172a" />
                </g>
                {/* Center Pivot Hub */}
                <circle cx="100" cy="90" r="7" fill="#0f172a" />
                <circle cx="100" cy="90" r="3" fill="#ffffff" />
              </svg>
            </div>
            <div className="cmp-exec-gauge-footer">
              <div className="cmp-exec-gauge-primary-val text-emerald">
                {calculatedKPIs?.financials?.roiMultiple || '0.0x'} Multiple
              </div>
              <div className="cmp-exec-gauge-subtext">Benchmark: &gt; 3.0x Return</div>
            </div>
          </div>

          {/* Radial Donut 1: Reachability Rate */}
          <div className="cmp-exec-gauge-card">
            <div className="cmp-exec-gauge-header">
              <span className="cmp-exec-gauge-title">
                <i className="fas fa-users" /> Reachability
              </span>
              <span className="cmp-exec-status-badge verified">
                <i className="fas fa-shield-check" /> Verified
              </span>
            </div>
            <div className="cmp-exec-donut-svg-wrap">
              <svg viewBox="0 0 120 120" className="cmp-exec-donut-svg">
                {/* Outer Dotted Reference Ring */}
                <circle
                  cx="60"
                  cy="60"
                  r="53"
                  fill="none"
                  stroke="#e2e8f0"
                  strokeWidth="1.5"
                  strokeDasharray="2 4"
                />
                {/* Background Circle */}
                <circle cx="60" cy="60" r="44" fill="none" stroke="#f1f5f9" strokeWidth="10" />
                {/* Foreground Progress Arc */}
                <circle
                  cx="60"
                  cy="60"
                  r="44"
                  fill="none"
                  stroke="#0284c7"
                  strokeWidth="10"
                  strokeDasharray={`${reachabilityDash} 300`}
                  strokeDashoffset="0"
                  strokeLinecap="round"
                  transform="rotate(-90 60 60)"
                  style={{ transition: 'stroke-dasharray 0.6s ease' }}
                />
                {/* Center Readout */}
                <text
                  x="60"
                  y="58"
                  textAnchor="middle"
                  fill="#0f172a"
                  fontSize="18"
                  fontWeight="800"
                  fontFamily="Inter, system-ui, sans-serif"
                >
                  {reachabilityRate}%
                </text>
                <text
                  x="60"
                  y="72"
                  textAnchor="middle"
                  fill="#64748b"
                  fontSize="9.5"
                  fontWeight="600"
                  fontFamily="Inter, system-ui, sans-serif"
                >
                  REACHABLE
                </text>
              </svg>
            </div>
            <div className="cmp-exec-gauge-footer">
              <div className="cmp-exec-gauge-primary-val text-blue">
                {reachableNumeric.toLocaleString()} / {audienceNumeric.toLocaleString()}
              </div>
              <div className="cmp-exec-gauge-subtext">Deliverable B2B Contacts</div>
            </div>
          </div>

          {/* Radial Donut 2: Meeting Yield Rate */}
          <div className="cmp-exec-gauge-card">
            <div className="cmp-exec-gauge-header">
              <span className="cmp-exec-gauge-title">
                <i className="fas fa-calendar-check" /> Meeting Yield
              </span>
              <span className="cmp-exec-status-badge active-conv">
                <i className="fas fa-bolt" /> {meetingRate}% Yield
              </span>
            </div>
            <div className="cmp-exec-donut-svg-wrap">
              <svg viewBox="0 0 120 120" className="cmp-exec-donut-svg">
                {/* Outer Dotted Reference Ring */}
                <circle
                  cx="60"
                  cy="60"
                  r="53"
                  fill="none"
                  stroke="#e2e8f0"
                  strokeWidth="1.5"
                  strokeDasharray="2 4"
                />
                {/* Background Circle */}
                <circle cx="60" cy="60" r="44" fill="none" stroke="#f1f5f9" strokeWidth="10" />
                {/* Foreground Progress Arc */}
                <circle
                  cx="60"
                  cy="60"
                  r="44"
                  fill="none"
                  stroke="#8b5cf6"
                  strokeWidth="10"
                  strokeDasharray={`${meetingYieldDash} 300`}
                  strokeDashoffset="0"
                  strokeLinecap="round"
                  transform="rotate(-90 60 60)"
                  style={{ transition: 'stroke-dasharray 0.6s ease' }}
                />
                {/* Center Readout */}
                <text
                  x="60"
                  y="58"
                  textAnchor="middle"
                  fill="#0f172a"
                  fontSize="18"
                  fontWeight="800"
                  fontFamily="Inter, system-ui, sans-serif"
                >
                  {meetingRate}%
                </text>
                <text
                  x="60"
                  y="72"
                  textAnchor="middle"
                  fill="#64748b"
                  fontSize="9.5"
                  fontWeight="600"
                  fontFamily="Inter, system-ui, sans-serif"
                >
                  YIELD RATE
                </text>
              </svg>
            </div>
            <div className="cmp-exec-gauge-footer">
              <div className="cmp-exec-gauge-primary-val text-purple">
                {meetingsNumeric} Meetings
              </div>
              <div className="cmp-exec-gauge-subtext">{opportunitiesNumeric} High-Intent Deals</div>
            </div>
          </div>
        </div>

        {/* Bottom Row: Metric Performance Rows with Sparklines and Bullet Range Bars */}
        <div className="cmp-exec-metric-rows-card">
          <div className="cmp-exec-metric-rows-header">
            <span className="cmp-exec-mr-title">
              Executive Milestone Trajectory &amp; Variance Analysis
            </span>
            <span className="cmp-exec-mr-sub">
              Real-time performance indicators against scenario benchmarks
            </span>
          </div>

          <div className="cmp-exec-table-layout">
            {/* Row 1: Pipeline Revenue Generation */}
            <div className="cmp-exec-metric-row">
              <div className="cmp-exec-col-indicator">
                <span
                  className="cmp-exec-indicator-icon success"
                  title="Performing Above Benchmark"
                >
                  ▲
                </span>
              </div>
              <div className="cmp-exec-col-info">
                <span className="cmp-exec-metric-label">Pipeline Value</span>
                <strong className="cmp-exec-metric-value">
                  {calculatedKPIs?.pipelineValue || 'RM 0'}
                </strong>
              </div>
              <div className="cmp-exec-col-sparkline">
                <svg viewBox="0 0 140 32" className="cmp-exec-mini-sparkline">
                  <defs>
                    <linearGradient id="miniSparkGrad1" x1="0" y1="0" x2="0" y2="1">
                      <stop offset="0%" stopColor="#10b981" stopOpacity="0.3" />
                      <stop offset="100%" stopColor="#10b981" stopOpacity="0.0" />
                    </linearGradient>
                  </defs>
                  <path
                    d="M 0 28 Q 35 26, 70 14 T 140 4 L 140 32 L 0 32 Z"
                    fill="url(#miniSparkGrad1)"
                  />
                  <path
                    d="M 0 28 Q 35 26, 70 14 T 140 4"
                    fill="none"
                    stroke="#10b981"
                    strokeWidth="2.2"
                    strokeLinecap="round"
                  />
                  <circle cx="140" cy="4" r="3" fill="#10b981" />
                </svg>
              </div>
              <div className="cmp-exec-col-bullet">
                <div className="cmp-bullet-wrapper">
                  <div className="cmp-bullet-track">
                    <div
                      className="cmp-bullet-fill emerald"
                      style={{
                        width: `${Math.min(100, Math.max(8, Math.round(pipelineAttainmentRatio * 75)))}%`,
                      }}
                    >
                      <div className="cmp-capsule-tooltip">
                        <span className="cmp-ct-dot emerald" />
                        <strong>{calculatedKPIs?.pipelineValue || 'RM 0'}</strong>
                        <span className="cmp-ct-sub">
                          ({Math.round(pipelineAttainmentRatio * 100)}% of Target)
                        </span>
                      </div>
                    </div>
                    <div className="cmp-bullet-marker" style={{ left: '75%' }}>
                      <div className="cmp-capsule-tooltip">
                        <span className="cmp-ct-dot blue" />
                        <span>
                          Target: <strong>{formatCurrency(pipelineTarget)}</strong>
                        </span>
                        <span className="cmp-ct-sub text-emerald">
                          {basePipelineNumeric >= pipelineTarget
                            ? `(+${formatCurrency(basePipelineNumeric - pipelineTarget)} ahead)`
                            : `(${formatCurrency(pipelineTarget - basePipelineNumeric)} remaining)`}
                        </span>
                      </div>
                    </div>
                  </div>
                  <div className="cmp-bullet-labels">
                    <span>RM 0</span>
                    <span className="target-txt">Target: {formatCurrency(pipelineTarget)}</span>
                    <span>{calculatedKPIs?.pipelineValue || 'RM 0'}</span>
                  </div>
                </div>
              </div>
            </div>

            {/* Row 2: Sales Qualified Meetings & Deals */}
            <div className="cmp-exec-metric-row">
              <div className="cmp-exec-col-indicator">
                <span
                  className="cmp-exec-indicator-icon success"
                  title="Positive Conversion Momentum"
                >
                  ▲
                </span>
              </div>
              <div className="cmp-exec-col-info">
                <span className="cmp-exec-metric-label">Qualified Meetings</span>
                <strong className="cmp-exec-metric-value">
                  {meetingsNumeric} {meetingsNumeric === 1 ? 'Session' : 'Sessions'}
                </strong>
              </div>
              <div className="cmp-exec-col-sparkline">
                {/* Mini Histogram */}
                <svg viewBox="0 0 140 32" className="cmp-exec-mini-histogram">
                  {[
                    { x: 5, h: 8 },
                    { x: 25, h: 14 },
                    { x: 45, h: 18 },
                    { x: 65, h: 22 },
                    { x: 85, h: 26 },
                    { x: 105, h: 28 },
                    { x: 125, h: 30 },
                  ].map((b, i) => (
                    <rect
                      key={i}
                      x={b.x}
                      y={32 - b.h}
                      width="12"
                      height={b.h}
                      rx="2.5"
                      fill="#0284c7"
                      opacity={0.4 + i * 0.1}
                    />
                  ))}
                </svg>
              </div>
              <div className="cmp-exec-col-bullet">
                <div className="cmp-bullet-wrapper">
                  <div className="cmp-bullet-track">
                    <div
                      className="cmp-bullet-fill blue"
                      style={{
                        width: `${Math.min(100, Math.max(10, Math.round((meetingsNumeric / (Math.max(1, Math.round(meetingsNumeric * 0.7)) * 1.4)) * 100)))}%`,
                      }}
                    >
                      <div className="cmp-capsule-tooltip">
                        <span className="cmp-ct-dot blue" />
                        <strong>
                          {meetingsNumeric} {meetingsNumeric === 1 ? 'Session' : 'Sessions'}
                        </strong>
                        <span className="cmp-ct-sub">
                          (
                          {Math.round(
                            (meetingsNumeric / Math.max(1, Math.round(meetingsNumeric * 0.7))) * 100
                          )}
                          % of Goal)
                        </span>
                      </div>
                    </div>
                    <div className="cmp-bullet-marker" style={{ left: '70%' }}>
                      {(() => {
                        const meetingGoal = Math.max(1, Math.round(meetingsNumeric * 0.7));
                        const diff = meetingsNumeric - meetingGoal;
                        return (
                          <div className="cmp-capsule-tooltip">
                            <span className="cmp-ct-dot purple" />
                            <span>
                              Goal:{' '}
                              <strong>
                                {meetingGoal} {meetingGoal === 1 ? 'Session' : 'Sessions'}
                              </strong>
                            </span>
                            <span className="cmp-ct-sub text-blue">
                              {diff > 0
                                ? `(+${diff} ahead)`
                                : diff === 0
                                  ? '(On target)'
                                  : `(${diff} behind)`}
                            </span>
                          </div>
                        );
                      })()}
                    </div>
                  </div>
                  <div className="cmp-bullet-labels">
                    <span>0</span>
                    <span className="target-txt">
                      Goal: {Math.max(1, Math.round(meetingsNumeric * 0.7))}
                    </span>
                    <span>{meetingsNumeric}</span>
                  </div>
                </div>
              </div>
            </div>

            {/* Row 3: Unit Economics - Cost Per Meeting */}
            <div className="cmp-exec-metric-row">
              <div className="cmp-exec-col-indicator">
                <span
                  className={`cmp-exec-indicator-icon ${cpmNumeric > 1500 ? 'warning' : 'neutral'}`}
                  title={
                    cpmNumeric > 1500 ? 'Exceeds target benchmark cap' : 'Within standard tolerance'
                  }
                >
                  {cpmNumeric > 1500 ? '!' : '●'}
                </span>
              </div>
              <div className="cmp-exec-col-info">
                <span className="cmp-exec-metric-label">Cost per Meeting</span>
                <strong className="cmp-exec-metric-value">
                  {calculatedKPIs?.financials?.formattedCostPerMeeting ||
                    formatCurrency(cpmNumeric)}
                </strong>
              </div>
              <div className="cmp-exec-col-sparkline">
                <svg viewBox="0 0 140 32" className="cmp-exec-mini-sparkline">
                  <path
                    d="M 0 8 Q 40 10, 80 20 T 140 24"
                    fill="none"
                    stroke="#0ea5e9"
                    strokeWidth="2.2"
                    strokeLinecap="round"
                  />
                  <circle cx="140" cy="24" r="3" fill="#0ea5e9" />
                </svg>
              </div>
              <div className="cmp-exec-col-bullet">
                <div className="cmp-bullet-wrapper">
                  <div className="cmp-bullet-track">
                    <div
                      className={`cmp-bullet-fill ${cpmNumeric > 1500 ? 'amber' : 'cyan'}`}
                      style={{
                        width: `${Math.min(100, Math.max(10, Math.round((cpmNumeric / (Math.max(cpmNumeric, 1500) * 1.15)) * 100)))}%`,
                      }}
                    >
                      <div className="cmp-capsule-tooltip">
                        <span className={`cmp-ct-dot ${cpmNumeric > 1500 ? 'amber' : 'cyan'}`} />
                        <strong>{formatCurrency(cpmNumeric)} / meeting</strong>
                        <span className="cmp-ct-sub">
                          {cpmNumeric > 1500
                            ? `(+RM ${cpmNumeric - 1500} over cap)`
                            : '(Within cap)'}
                        </span>
                      </div>
                    </div>
                    <div
                      className="cmp-bullet-marker"
                      style={{
                        left: `${Math.min(95, Math.max(15, Math.round((1500 / (Math.max(cpmNumeric, 1500) * 1.15)) * 100)))}%`,
                      }}
                    >
                      <div className="cmp-capsule-tooltip">
                        <span className="cmp-ct-dot amber" />
                        <span>
                          Cap: <strong>RM 1,500</strong>
                        </span>
                        <span className="cmp-ct-sub text-amber">(Target Ceiling)</span>
                      </div>
                    </div>
                  </div>
                  <div className="cmp-bullet-labels">
                    <span>RM 500</span>
                    <span className="target-txt">Cap: RM 1,500</span>
                    <span>
                      {calculatedKPIs?.financials?.formattedCostPerMeeting ||
                        formatCurrency(cpmNumeric)}
                    </span>
                  </div>
                </div>
              </div>
            </div>

            {/* Row 4: Budget Deployment & Efficiency */}
            <div className="cmp-exec-metric-row">
              <div className="cmp-exec-col-indicator">
                <span className="cmp-exec-indicator-icon success" title="100% Capital Allocated">
                  ▲
                </span>
              </div>
              <div className="cmp-exec-col-info">
                <span className="cmp-exec-metric-label">Allocated Budget</span>
                <strong className="cmp-exec-metric-value">{formatCurrency(budgetNumeric)}</strong>
              </div>
              <div className="cmp-exec-col-sparkline">
                <svg viewBox="0 0 140 32" className="cmp-exec-mini-sparkline">
                  <path
                    d="M 0 28 L 30 20 L 70 14 L 110 8 L 140 6"
                    fill="none"
                    stroke="#f59e0b"
                    strokeWidth="2.2"
                    strokeDasharray="4 3"
                  />
                  <circle cx="140" cy="6" r="3" fill="#f59e0b" />
                </svg>
              </div>
              <div className="cmp-exec-col-bullet">
                <div className="cmp-bullet-wrapper">
                  <div className="cmp-bullet-track">
                    <div className="cmp-bullet-fill amber" style={{ width: '100%' }}>
                      <div className="cmp-capsule-tooltip">
                        <span className="cmp-ct-dot amber" />
                        <strong>{formatCurrency(budgetNumeric)}</strong>
                        <span className="cmp-ct-sub text-emerald">(100% Allocated)</span>
                      </div>
                    </div>
                    <div className="cmp-bullet-marker" style={{ left: '100%' }}>
                      <div className="cmp-capsule-tooltip">
                        <span className="cmp-ct-dot emerald" />
                        <span>
                          Committed: <strong>{formatCurrency(budgetNumeric)}</strong>
                        </span>
                      </div>
                    </div>
                  </div>
                  <div className="cmp-bullet-labels">
                    <span>RM 0</span>
                    <span className="target-txt">100% Planned</span>
                    <span>{formatCurrency(budgetNumeric)}</span>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
}
